import React, { useState } from 'react';
import {
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  Badge,
  Button
} from 'reactstrap';
import moment from 'moment';

const deliveryStatusColor = (status) => {
  if (status === 'read') return 'info';
  if (status === 'delivered') return 'primary';
  if (status === 'sent') return 'warning';
  if (status === 'failed') return 'danger';
  return 'secondary';
};

const RECIPIENT_LABELS = {
  offering: 'Offerings',
  batch: 'Batches',
  child: 'Children',
  phone: 'Phone numbers'
};

function formatRecipientList(commData) {
  const { recipientType, recipientNames, phoneNumbers } = commData;
  const names = Array.isArray(recipientNames) ? recipientNames.filter(Boolean) : [];

  if (recipientType === 'phone') {
    const phones = Array.isArray(phoneNumbers) ? phoneNumbers.filter(Boolean) : [];
    if (phones.length === 0) return '—';
    return phones.join(', ');
  }

  if (names.length > 0) {
    return names.join(', ');
  }

  return '—';
}

const ViewWhatsAppModal = ({ isOpen, handleClose, commData, onRefresh }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (!onRefresh) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  if (!commData) return null;

  const toggleRow = (idx) => setExpandedRow((prev) => (prev === idx ? null : idx));
  const recipientLabel = RECIPIENT_LABELS[commData.recipientType] || 'Recipients';
  const recipientList = formatRecipientList(commData);

  return (
    <Modal isOpen={isOpen} backdrop="static" size="lg">
      <ModalHeader toggle={handleClose}>
        <span>WhatsApp Communication Details</span>
        {onRefresh && (
          <Button
            color="outline-secondary"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            style={{ marginLeft: '1rem', fontSize: '0.78rem' }}
          >
            <i className={`fas fa-sync-alt${refreshing ? ' fa-spin' : ''} mr-1`}></i>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        )}
      </ModalHeader>
      <ModalBody>
        <Row className="mb-3" style={{ fontSize: '0.9rem' }}>
          <Col md="3">
            <span className="text-muted">Recipient Type</span>
            <div><strong>{commData.recipientType || '—'}</strong></div>
          </Col>
          <Col md="3">
            <span className="text-muted">Status</span>
            <div>
              <Badge
                color={
                  commData.status === 'sent' ? 'success'
                  : commData.status === 'failed' ? 'danger'
                  : 'warning'
                }
              >
                {commData.status}
              </Badge>
            </div>
          </Col>
          <Col md="3">
            <span className="text-muted">Sent By</span>
            <div><strong>{commData.sentByName || 'N/A'}</strong></div>
          </Col>
          <Col md="3">
            <span className="text-muted">Message</span>
            <div><strong>{commData.message || '—'}</strong></div>
          </Col>
        </Row>

        <Row className="mb-3" style={{ fontSize: '0.9rem' }}>
          <Col md={commData.purpose ? 6 : 12}>
            <span className="text-muted">{recipientLabel}</span>
            <div><strong>{recipientList}</strong></div>
          </Col>
          {commData.purpose && (
            <Col md="6">
              <span className="text-muted">Purpose</span>
              <div><strong>{commData.purpose}</strong></div>
            </Col>
          )}
        </Row>

        {commData.auditLogs && commData.auditLogs.length > 0 && (
          <Row className="mt-3">
            <Col md="12">
              <h6>Delivery Log</h6>
              <div style={{ maxHeight: '20rem', overflow: 'auto' }}>
                <Table striped bordered responsive size="sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Recipient</th>
                      <th>Status</th>
                      <th>Last Updated</th>
                      <th>Error</th>
                      <th>History</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commData.auditLogs.map((log, idx) => {
                      const status = log.deliveryStatus || (log.success ? 'sent' : 'failed');
                      const updatedAt = log.statusTimestamp
                        ? moment(log.statusTimestamp).format('DD MMM YYYY HH:mm')
                        : '—';

                      const sentEntry = log.timestamp
                        ? { status: 'sent', timestamp: log.timestamp }
                        : null;
                      const history = [
                        ...(sentEntry ? [sentEntry] : []),
                        ...(log.statusHistory || [])
                      ].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

                      const isExpanded = expandedRow === idx;

                      return (
                        <React.Fragment key={idx}>
                          <tr>
                            <td>{idx + 1}</td>
                            <td>
                              {log.childName ? (
                                <div>
                                  <div style={{ fontWeight: 600 }}>{log.childName}</div>
                                  <div style={{ fontSize: '0.78rem', color: '#6c757d' }}>{log.phoneNumber}</div>
                                </div>
                              ) : (
                                log.phoneNumber
                              )}
                            </td>
                            <td>
                              <Badge color={deliveryStatusColor(status)}>{status}</Badge>
                            </td>
                            <td>{updatedAt}</td>
                            <td>{log.error || '-'}</td>
                            <td>
                              <span
                                style={{ cursor: 'pointer', color: '#007bff', fontSize: '0.8rem' }}
                                onClick={() => toggleRow(idx)}
                              >
                                {isExpanded ? '▲ Hide' : '▼ Show'}
                              </span>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={6} style={{ background: '#f8f9fa', padding: '0.5rem 1rem' }}>
                                {history.length === 0 ? (
                                  <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                                    No history available for this message.
                                  </span>
                                ) : (
                                  <div style={{ fontSize: '0.82rem' }}>
                                    {history.map((entry, i) => (
                                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                                        <Badge color={deliveryStatusColor(entry.status)} style={{ minWidth: '4.5rem', textAlign: 'center' }}>
                                          {entry.status}
                                        </Badge>
                                        <span className="text-muted">
                                          {entry.timestamp
                                            ? moment(entry.timestamp).format('DD MMM YYYY HH:mm:ss')
                                            : '—'}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ViewWhatsAppModal;
